import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TaskPayReportRowType } from '@econtractor/types';
import * as apiReport from 'api/apiReports';
import { useToast } from 'context/ToastContext';
import { useTranslation } from 'react-i18next';
import type { ReportDateFilterState } from '../../helper/reportObjectsStorage';
import {
    loadReportTasksFilters,
    saveReportTasksFilters,
    type ReportTasksViewMode,
} from '../../helper/reportTasksStorage';
import { filterTasksByStatus } from './filterByStatus';

export const useReportByTask = () => {
    const { t } = useTranslation(['Reports']);
    const { showToast } = useToast();
    const savedFilters = useMemo(() => loadReportTasksFilters(), []);

    const [users, setUsers] = useState<TaskPayReportRowType[] | null>(null);
    const [isReporting, setIsReporting] = useState(false);
    const [dates, setDates] = useState<[number, number] | null>(savedFilters.dates);
    const [viewMode, setViewMode] = useState<ReportTasksViewMode>(savedFilters.viewMode);
    const [statuses, setStatuses] = useState<number[]>(savedFilters.statuses);
    const [showZeroTasks, setShowZeroTasks] = useState(savedFilters.showZeroTasks);
    const [dateFilter, setDateFilter] = useState<ReportDateFilterState>({
        period: savedFilters.period,
        dates: savedFilters.dates,
        customRange: savedFilters.customRange,
    });

    const fromMs = dates?.[0] ?? null;
    const toMs = dates?.[1] ?? null;

    const persist = useCallback(
        (patch: Partial<{
            dateFilter: ReportDateFilterState;
            viewMode: ReportTasksViewMode;
            statuses: number[];
            showZeroTasks: boolean;
        }>) => {
            const nextFilter = patch.dateFilter ?? dateFilter;
            const nextView = patch.viewMode ?? viewMode;
            const nextStatuses = patch.statuses ?? statuses;
            const nextShowZero = patch.showZeroTasks ?? showZeroTasks;
            if (!nextFilter.dates?.[0] || !nextFilter.dates?.[1]) return;
            saveReportTasksFilters({
                period: nextFilter.period,
                dates: [nextFilter.dates[0], nextFilter.dates[1]],
                customRange: nextFilter.customRange,
                viewMode: nextView,
                statuses: nextStatuses,
                showZeroTasks: nextShowZero,
            });
        },
        [dateFilter, viewMode, statuses, showZeroTasks],
    );

    const handleDateFilterChange = (filter: ReportDateFilterState) => {
        setDateFilter(filter);
        setDates(filter.dates);
        persist({ dateFilter: filter });
    };

    const handleViewModeToggle = () => {
        const next: ReportTasksViewMode = viewMode === 'detail' ? 'summary' : 'detail';
        setViewMode(next);
        persist({ viewMode: next });
    };

    const handleStatusesChange = (next: number[]) => {
        setStatuses(next);
        persist({ statuses: next });
    };

    const handleShowZeroTasksChange = (next: boolean) => {
        setShowZeroTasks(next);
        persist({ showZeroTasks: next });
    };

    useEffect(() => {
        const load = async () => {
            if (!fromMs || !toMs) return;
            setIsReporting(true);
            const resRep = await apiReport.get4Tasks(fromMs, toMs);
            if (!resRep) showToast(t('common:messError'), 'danger');
            setUsers(resRep?.users ?? []);
            setIsReporting(false);
        };
        void load();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when the period changes
    }, [fromMs, toMs]);

    const displayedUsers = useMemo(
        () => (users ? filterTasksByStatus(users, statuses, showZeroTasks) : null),
        [users, statuses, showZeroTasks],
    );

    return {
        users: displayedUsers,
        isReporting,
        dates,
        setDates,
        dateFilter,
        viewMode,
        statuses,
        showZeroTasks,
        handleDateFilterChange,
        handleViewModeToggle,
        handleStatusesChange,
        handleShowZeroTasksChange,
    };
};
